import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EvaluationService } from '@services/core/evaluation.service';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '@services/core/user.service';
import Swal from 'sweetalert2';
import { EvaluationCategoryComponent } from './evaluation-category.component';
import { periodMonthLabel } from '@shared/period.utils';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { findLevel } from '@shared/evaluation-scoring.utils';
import { applyGoalsToState, applyYtdToState, clearGoalSets, clearYtdState } from '@shared/evaluation-goal.helpers';
import { collectCompletedIds } from '@shared/evaluation-goal.helpers';
import { applyLiveScoreTo, writePreviewScore } from '@shared/evaluation-preview.helpers';
import { applyMonthSelection, prependPeriod, storePeriodResult } from './evaluation-fill-state.helpers';
import { currentMonth, findPeriodByMonth, periodToMonth, isPeriodLocked } from '@shared/evaluation-period.helpers';
import { canReviewPeriod, currentUserId, isRawTooHigh, reviewFinalScore } from '@shared/evaluation-review.helpers';
import { reviewPenaltyFor, signErrorText } from '@shared/evaluation-review.helpers';

@Component({
  selector: 'app-evaluation-fill',
  templateUrl: './evaluation-fill.component.html',
  styleUrls: ['./evaluation-fill.component.scss'],
  imports: [PanelComponent, FormsModule, DatePipe, EvaluationCategoryComponent, NgbTooltipModule],
  providers: [EvaluationService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class EvaluationFillComponent implements OnInit {
  protected module: any;
  protected routeSegment = '';
  protected periods: any[] = [];
  protected selectedPeriod: any = null;
  protected selectedMonth = '';
  protected maxMonth = new Date().toISOString().slice(0, 7);
  protected noPeriodForMonth = false;
  protected period: any = null;
  protected evaluation: any[] = [];
  protected loading = true;
  protected missingGoalIds = new Set<string>();
  protected completedGoalIds = new Set<string>();
  protected reviewMode = false;
  protected reviewInput: number | null = null;
  protected reviewEntryMode: 'percent' | 'raw' = 'percent';
  private goalNumbers: Record<string, number> = {};
  private ytdBase: Record<string, number> = {};
  private ytdAnswerBase: Record<string, number> = {};
  private liveScoreTimer: any = null;
  private evaluationService = inject(EvaluationService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => this.applyFillData(data),
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (params) => this.applyQueryParams(params),
    });
  }
  private applyFillData(data: any): void {
    const fill = data['fillData'] || {};
    const segment = fill.segment ?? (this.route.snapshot.paramMap.get('moduleRoute') || '');
    if (this.sameModule(fill.module)) return;
    this.resetFillState(segment, fill.module);
  }
  private sameModule(next: any): boolean { return next?.id === this.module?.id && this.module; }
  private resetFillState(segment: string, module: any): void {
    this.routeSegment = segment;
    this.module = module;
    this.periods = [];
    this.period = null;
    this.evaluation = [];
    this.selectedPeriod = null;
    this.selectedMonth = '';
    this.noPeriodForMonth = false;
    this.loading = true;
    if (this.module) this.loadPeriods();
    else this.loading = false;
  }
  private applyQueryParams(params: any): void {
    const periodId = params.get('periodId');
    this.reviewMode = params.get('review') === '1';
    if (periodId && periodId !== this.period?.id) this.loadPeriodById(periodId);
  }
  private syncMonth(): void { this.selectedMonth = currentMonth(this.period, this.selectedPeriod, this.selectedMonth); }
  private loadPeriodById(periodId: string): void {
    this.loading = true;
    this.evaluationService.getPeriodById(periodId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applyPeriodById(res),
      error: () => { this.loading = false; },
    });
  }
  private applyPeriodById(res: any): void {
    storePeriodResult(this, res);
    prependPeriod(this, res.period);
    this.selectedPeriod = res.period;
    this.refreshMissingGoals();
    this.refreshYtdBase();
  }
  private loadPeriods(): void {
    this.evaluationService.getMyPeriods(this.module.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows: any[]) => this.applyPeriods(rows || []),
      error: () => { if (!this.route.snapshot.queryParamMap.get('periodId')) this.loadLatest(); },
    });
  }
  private applyPeriods(rows: any[]): void {
    this.periods = rows;
    const periodId = this.route.snapshot.queryParamMap.get('periodId');
    if (!periodId) this.selectLatest();
    else this.syncMonth();
  }
  private selectLatest(): void {
    if (!this.periods.length) { this.loadLatest(); return; }
    this.selectedPeriod = this.periods[0];
    this.syncMonth();
    this.noPeriodForMonth = false;
    this.loadEvaluation(this.selectedPeriod.period);
  }
  private loadLatest(): void {
    this.loading = true;
    this.evaluationService.getLatestPeriodByModule(this.module.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applyEvaluation(res),
      error: () => { this.loading = false; },
    });
  }
  protected periodLabel(p: any): string { return periodMonthLabel(p?.period); }
  protected isLocked(): boolean { return isPeriodLocked(this.period); }
  onPeriodChange(): void {
    if (!this.selectedPeriod) return;
    this.selectedMonth = periodToMonth(this.selectedPeriod.period);
    this.noPeriodForMonth = false;
    this.loadEvaluation(this.selectedPeriod.period);
  }
  protected onMonthChange(): void {
    if (!this.selectedMonth) return;
    applyMonthSelection(this, findPeriodByMonth(this.periods, this.selectedMonth), (d) => this.loadEvaluation(d));
  }
  private loadEvaluation(periodDate: string): void {
    this.loading = true;
    this.noPeriodForMonth = false;
    const iso = String(periodDate).split('T')[0];
    this.evaluationService.getPeriod(iso, null, this.module.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.applyEvaluation(res),
      error: () => { this.loading = false; },
    });
  }
  private applyEvaluation(res: any): void {
    storePeriodResult(this, res);
    this.reviewEntryMode = 'percent';
    this.reviewInput = 0;
    this.refreshMissingGoals();
    this.refreshYtdBase();
  }
  private isAnnual(): boolean { return this.module?.scoringMode === 'annual'; }
  private refreshMissingGoals(): void {
    if (!this.isAnnual()) clearGoalSets(this);
    else if (this.period?.entityId && this.period?.year) this.loadMissingGoals();
  }
  private loadMissingGoals(): void {
    this.evaluationService.getPersonalGoals(this.module.id, this.period.entityId, this.period.year)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (goals: any[]) => { applyGoalsToState(this, goals); this.refreshCompletedGoals(); } });
  }
  private refreshCompletedGoals(): void {
    if (!this.isAnnual()) this.completedGoalIds = new Set();
    else this.completedGoalIds = collectCompletedIds(this.evaluation, this.ytdAnswerBase, this.goalNumbers);
  }
  private refreshYtdBase(): void {
    if (!this.isAnnual() || !this.period?.id) clearYtdState(this);
    else this.loadYtdBase();
  }
  private loadYtdBase(): void {
    this.evaluationService.getYtdBase(this.period.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res: any) => { applyYtdToState(this, res); this.refreshCompletedGoals(); this.updatePreviewScore(); } });
  }
  protected onAnswerChanged(): void {
    this.updatePreviewScore();
    this.scheduleLiveScore();
  }
  private updatePreviewScore(): void {
    if (!this.period || !this.evaluation?.length) return;
    writePreviewScore(this, this.isAnnual());
  }
  private scheduleLiveScore(): void {
    if (!this.period?.id) return;
    this.restartLiveTimer();
  }
  private restartLiveTimer(): void {
    if (this.liveScoreTimer) clearTimeout(this.liveScoreTimer);
    this.liveScoreTimer = setTimeout(() => this.fetchLiveScore(), 500);
  }
  private fetchLiveScore(): void {
    this.evaluationService.getLiveScore(this.period.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res: any) => applyLiveScoreTo(this, res) });
  }
  protected levelForScore(): any | null { return findLevel(this.period?.score ?? 0, this.module?.levels || []); }
  protected scoreColor(): string {
    const lvl = this.levelForScore();
    return lvl?.color || '#dc3545';
  }
  protected scoreLabel(): string | null {
    const lvl = this.levelForScore();
    return lvl ? lvl.label : this.period?.level || null;
  }
  protected hasMissingGoal(qId: string): boolean { return this.missingGoalIds.has(qId); }
  protected finish(): void {
    if (!this.period || this.isLocked()) return;
    this.evaluationService.finishPeriod({ id: this.period.id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadEvaluation(this.period.period),
      error: () => {},
    });
  }
  protected canReview(): boolean { return canReviewPeriod(this.reviewMode, this.period, currentUserId(this.userService)); }
  protected isSigned(): boolean { return Boolean(this.period?.signedStatus); }
  protected baseScore(): number { return Number(this.period?.score || 0); }
  protected reviewPenalty(): number { return reviewPenaltyFor(this.reviewEntryMode, this.reviewInput, this.baseScore()); }
  protected reviewFinal(): number { return reviewFinalScore(this.baseScore(), this.reviewPenalty()); }
  protected rawTooHigh(): boolean { return isRawTooHigh(this.reviewEntryMode, this.reviewInput, this.baseScore()); }
  protected approveReview(): void {
    if (!this.canReview() || !this.period) return;
    this.runSign({ id: this.period.id, status: true, penaltyPercent: this.reviewPenalty() });
  }
  protected rejectReview(): void {
    if (!this.canReview() || !this.period) return;
    Swal.fire({ title: 'Rechazar periodo', text: 'Se reabrirá el periodo para que la entidad corrija su evaluación.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Rechazar', cancelButtonText: 'Cancelar' }).then((r) => { if (r?.isConfirmed) this.runSign({ id: this.period.id, status: false }); });
  }
  private runSign(payload: any): void {
    this.evaluationService.signPeriod(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.afterReview(res),
      error: (err) => Swal.fire({ icon: 'error', title: 'Error', text: signErrorText(err) }),
    });
  }
  private afterReview(res: any): void {
    Swal.fire({ icon: 'success', title: 'Revisión registrada', timer: 1500, showConfirmButton: false });
    if (res?.status === false) this.router.navigate([`/${this.routeSegment}/periodos`]);
    else this.loadPeriodById(this.period.id);
  }
}
